// USGA handicap calculation utilities

/**
 * Format a handicap index for display using golf convention.
 * Negative values (plus handicaps) display with a "+" prefix: -1.3 → "+1.3"
 * Positive values display as-is: 7.1 → "7.1"
 */
export function formatHandicapIndex(value) {
  const num = Number(value)
  if (isNaN(num)) return '—'
  if (num < 0) return `+${Math.abs(num).toFixed(1)}`
  return num.toFixed(1)
}

/**
 * Check whether a handicap index value is set (non-null and non-zero).
 * Works for both positive and negative (plus) handicap indices.
 */
export function hasHandicapIndex(value) {
  return value != null && Number(value) !== 0
}

/**
 * Split a stored handicap index into { isPlus, magnitude } for a sign-toggle UI.
 * Plus handicaps are stored as negative numbers, so a stored -1.4 returns
 * { isPlus: true, magnitude: '1.4' }. Used to pre-populate a magnitude-only
 * number input alongside a "+ Plus" toggle.
 */
export function splitHandicapForInput(stored) {
  const num = Number(stored)
  if (!isFinite(num)) return { isPlus: false, magnitude: '' }
  if (num === 0) return { isPlus: false, magnitude: '' }
  return { isPlus: num < 0, magnitude: Math.abs(num).toFixed(1) }
}

/**
 * Combine a magnitude string + isPlus flag into a stored handicap index.
 * Returns null for blank or invalid input so the caller can decide whether
 * to skip the field. Negative magnitudes are rejected — sign comes from isPlus.
 */
export function combineHandicapInput({ isPlus, magnitude }) {
  if (magnitude === '' || magnitude == null) return null
  const n = parseFloat(magnitude)
  if (isNaN(n) || n < 0) return null
  return isPlus ? -Math.abs(n) : n
}

/**
 * Resolve the correct slope/rating for a player based on gender.
 * Women's ratings are stored as womenSlope/womenRating on the tee object.
 * Falls back to men's (default) ratings if women's data is not available.
 */
export function resolvePlayerTeeRatings(tee, gender) {
  if (gender === 'F' && tee.womenSlope != null && tee.womenRating != null) {
    return { slope: tee.womenSlope, rating: tee.womenRating }
  }
  return { slope: tee.slope, rating: tee.rating }
}

/**
 * Resolve the correct hole handicap rankings for a player based on gender.
 * Women's rankings are stored as womenHandicapIndex on the tee object.
 * Falls back to men's (default) rankings if women's data is not available.
 */
export function resolvePlayerHandicapIndex(tee, gender, fallbackRankings) {
  if (gender === 'F' && tee?.womenHandicapIndex?.length >= 18) {
    return tee.womenHandicapIndex
  }
  return tee?.handicapIndex || fallbackRankings
}

/**
 * Calculate course handicap from handicap index using USGA formula:
 * Course Handicap = Handicap Index × (Slope Rating / 113) + (Course Rating - Par)
 * Result is rounded to nearest whole number.
 */
export function calculateCourseHandicap(handicapIndex, slope, rating, par) {
  const raw = handicapIndex * (slope / 113) + (rating - par)
  return Math.round(raw)
}

/**
 * Adjust all course handicaps to play off the low handicap of the group.
 * The lowest handicap player gets 0 strokes, everyone else gets the difference.
 * @param {Array} playerHandicaps - [{ playerId, courseHandicap, ... }]
 * @param {number} [minOverride] - Optional external floor (e.g. session-wide low CH).
 *   When provided, differences are computed from this value instead of the group minimum.
 * Returns array of { playerId, courseHandicap, adjustedStrokes }
 */
export function adjustHandicapsOffLow(playerHandicaps, minOverride) {
  const minHandicap = minOverride ?? Math.min(...playerHandicaps.map((p) => p.courseHandicap))
  return playerHandicaps.map((p) => ({
    ...p,
    adjustedStrokes: p.courseHandicap - minHandicap,
  }))
}

/**
 * Given a player's adjusted stroke count and the hole handicap rankings (1-18),
 * returns a Set of hole numbers (1-indexed) where the player gets a stroke.
 * Strokes are applied to holes ranked 1 through adjustedStrokes.
 * If adjustedStrokes > 18, the player gets an extra stroke on holes ranked
 * 1 through (adjustedStrokes - 18), i.e., two strokes on the hardest holes.
 */
export function getStrokeHoles(adjustedStrokes, holeHandicapRankings) {
  const strokeHoles = new Map() // holeNumber -> number of strokes
  const firstPass = Math.min(adjustedStrokes, 18)
  const secondPass = Math.max(adjustedStrokes - 18, 0)

  for (let holeIdx = 0; holeIdx < holeHandicapRankings.length; holeIdx++) {
    const ranking = holeHandicapRankings[holeIdx]
    const holeNumber = holeIdx + 1
    let strokes = 0
    if (ranking <= firstPass) strokes += 1
    if (ranking <= secondPass) strokes += 1
    if (strokes > 0) strokeHoles.set(holeNumber, strokes)
  }

  return strokeHoles
}

/**
 * Calculate net score for a hole.
 * @param {number} grossScore - The actual strokes taken
 * @param {number} strokesOnHole - Number of handicap strokes received on this hole (0, 1, or 2)
 * @returns {number} Net score
 */
export function netScore(grossScore, strokesOnHole = 0) {
  return grossScore - strokesOnHole
}

/**
 * Build a course handicap breakdown for stroke play games (no off-low adjustment).
 * Used for Net Stroke Play, Duels, Six Pack, Stableford, and Low Net side games.
 * Playing Handicap = round(Course Handicap × percentage)
 * @param {Array} players - [{ playerId, handicapIndex }]
 * @param {number} slope - Course slope rating
 * @param {number} rating - Course rating
 * @param {number} par - Course par
 * @param {Array} holeHandicapRankings - 18-element array of hole handicap rankings
 * @param {number} handicapPercent - Percentage of course handicap to use (default 1.0)
 * @returns {Array} [{ playerId, handicapIndex, courseHandicap, adjustedStrokes, strokeHoles }]
 */
export function buildFullHandicapBreakdown(players, slope, rating, par, holeHandicapRankings, handicapPercent = 1.0) {
  return players.map((p) => {
    const courseHandicap = calculateCourseHandicap(p.handicapIndex, slope, rating, par)
    const adjustedStrokes = Math.round(courseHandicap * handicapPercent)
    return {
      ...p,
      courseHandicap,
      adjustedStrokes,
      strokeHoles: getStrokeHoles(adjustedStrokes, holeHandicapRankings),
    }
  })
}

/**
 * Build a handicap breakdown using the WHS 2024 differential method for match play.
 * 1. Calculate full Course Handicap for each player
 * 2. Off-low: lowest CH plays at 0, others get the difference
 * 3. Apply percentage to the differences (not to CH)
 * 4. Round to nearest whole number
 * 5. Allocate strokes by hole handicap ranking
 *
 * Note: At 100% (Solo Match Play), the order doesn't matter — result is identical.
 *
 * @param {Array} players - [{ playerId, handicapIndex }]
 * @param {number} slope - Course slope rating
 * @param {number} rating - Course rating
 * @param {number} par - Course par
 * @param {Array} holeHandicapRankings - 18-element array of hole handicap rankings
 * @param {number} handicapPercent - Percentage applied to the differential (default 1.0)
 * @returns {Array} [{ playerId, handicapIndex, courseHandicap, adjustedStrokes, strokeHoles }]
 */
export function buildHandicapBreakdown(players, slope, rating, par, holeHandicapRankings, handicapPercent = 1.0, minCourseHandicap) {
  const withCourseHandicaps = players.map((p) => ({
    ...p,
    courseHandicap: calculateCourseHandicap(p.handicapIndex, slope, rating, par),
  }))

  // Off-low first: get differences from lowest CH (or session-wide low if provided)
  const adjusted = adjustHandicapsOffLow(withCourseHandicaps, minCourseHandicap)

  // Apply percentage to the differences, then round
  return adjusted.map((p) => {
    const adjustedStrokes = Math.round(p.adjustedStrokes * handicapPercent)
    return {
      ...p,
      adjustedStrokes,
      strokeHoles: getStrokeHoles(adjustedStrokes, holeHandicapRankings),
    }
  })
}

/**
 * Build handicap breakdown for 2v2 Scramble using USGA recommended allowances.
 * 2-person formula: 35% of lower course handicap + 15% of higher = team handicap.
 * Off-low between teams: lower team plays at 0, higher gets the difference.
 * Stroke holes allocated from hole handicap rankings.
 * Both players on a team share the same adjustedStrokes and strokeHoles.
 *
 * @param {Array} teams - [{ playerIds: [uuid, uuid] }]
 * @param {Array} players - [{ playerId, handicapIndex }]
 * @param {number} slope - Course slope rating
 * @param {number} rating - Course rating
 * @param {number} par - Course par
 * @param {Array} holeHandicapRankings - 18-element array of hole handicap rankings
 * @param {boolean} straightUp - If true, no handicap applied (team handicap = 0)
 * @returns {Array} [{ playerId, handicapIndex, courseHandicap, adjustedStrokes, strokeHoles }]
 */
export function buildScrambleHandicapBreakdown(teams, players, slope, rating, par, holeHandicapRankings, straightUp = false) {
  // Step 1: Compute each player's full course handicap
  const withCourseHandicaps = players.map((p) => ({
    ...p,
    courseHandicap: calculateCourseHandicap(p.handicapIndex, slope, rating, par),
  }))

  if (straightUp) {
    return withCourseHandicaps.map((p) => ({
      ...p,
      adjustedStrokes: 0,
      strokeHoles: new Map(),
    }))
  }

  // Step 2: Compute team handicaps using USGA scramble weights (35% low + 15% high)
  const teamHandicaps = teams.map((team) => {
    const teamPlayers = team.playerIds
      .map((pid) => withCourseHandicaps.find((p) => p.playerId === pid))
      .filter(Boolean)
      .sort((a, b) => a.courseHandicap - b.courseHandicap)
    if (teamPlayers.length < 2) return 0
    return Math.round(teamPlayers[0].courseHandicap * 0.35 + teamPlayers[1].courseHandicap * 0.15)
  })

  // Step 3: Off-low between teams
  const minTeamHandicap = Math.min(...teamHandicaps)
  const teamAdjusted = teamHandicaps.map((th) => th - minTeamHandicap)

  // Step 4: Build player-level breakdown (both players on a team share the team's strokes)
  const teamPlayerMap = new Map()
  teams.forEach((team, idx) => {
    team.playerIds.forEach((pid) => teamPlayerMap.set(pid, idx))
  })

  return withCourseHandicaps.map((p) => {
    const teamIdx = teamPlayerMap.get(p.playerId)
    const adjustedStrokes = teamIdx != null ? teamAdjusted[teamIdx] : 0
    return {
      ...p,
      adjustedStrokes,
      strokeHoles: getStrokeHoles(adjustedStrokes, holeHandicapRankings),
    }
  })
}

/**
 * Build handicap breakdown for 3v4 games where each team uses a different percentage.
 * Match play (Top 2): 3-man = 90%, 4-man = 80%, differential method (off-low first, then %)
 * Stroke play (Stableford): 3-man = 85%, 4-man = 75%, percentage of CH (no off-low)
 * @param {Map<string, number>} playerPercentMap - playerId -> handicap percentage
 * @param {boolean} offLow - Whether to use differential method (match play) or direct percentage (stroke play)
 */
export function buildTeamHandicapBreakdown(players, slope, rating, par, holeHandicapRankings, playerPercentMap, offLow = true, minCourseHandicap) {
  const withCourseHandicaps = players.map((p) => ({
    ...p,
    courseHandicap: calculateCourseHandicap(p.handicapIndex, slope, rating, par),
  }))

  if (offLow) {
    // Differential method: off-low first across all players, then apply team-specific percentages to differences
    const adjusted = adjustHandicapsOffLow(withCourseHandicaps, minCourseHandicap)
    return adjusted.map((p) => {
      const percent = playerPercentMap.get(p.playerId) || 1.0
      const adjustedStrokes = Math.round(p.adjustedStrokes * percent)
      return {
        ...p,
        adjustedStrokes,
        strokeHoles: getStrokeHoles(adjustedStrokes, holeHandicapRankings),
      }
    })
  }

  // Stroke play: apply percentage directly to course handicap (no off-low)
  return withCourseHandicaps.map((p) => {
    const percent = playerPercentMap.get(p.playerId) || 1.0
    const adjustedStrokes = Math.round(p.courseHandicap * percent)
    return {
      ...p,
      adjustedStrokes,
      strokeHoles: getStrokeHoles(adjustedStrokes, holeHandicapRankings),
    }
  })
}
